import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { useAuth } from '../../contexts/AuthContext';
import { employeesAPI } from '../../services/employeesService';
import EmployeesTable from './EmployeesTable';
import EmployeeFormDialog from './EmployeeFormDialog';
import EmployeeDetails from './EmployeeDetails';
import AttendanceDialog from './AttendanceDialog';
import SalaryDialog from './SalaryDialog';
import SalaryHistoryDialog from './SalaryHistoryDialog';
import EmployeeFilter from './EmployeeFilter';

const Employees = () => {
  const { user } = useAuth();

  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Lazy loading params for server-side pagination / filtering / sorting
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 3,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: 'active', matchMode: FilterMatchMode.EQUALS }
    }
  });

  // Dialog states
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [attendanceDialog, setAttendanceDialog] = useState(false);
  const [salaryDialog, setSalaryDialog] = useState(false);
  const [salarySummary, setSalarySummary] = useState({ pending: 0, processed: 0, paid: 0, total: 0 });
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [salaryHistoryDialog, setSalaryHistoryDialog] = useState(false);
  const [paymentMode, setPaymentMode] = useState('Cash');

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showDetailsView, setShowDetailsView] = useState(false);
  const [detailsEmployee, setDetailsEmployee] = useState(null);

  // Form state used by Add/Edit dialog
  const initialEmployeeForm = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    departmentId: '',
    designation: '',
    joiningDate: null,
    salary: null,
    photoUrl: '',
    // status: "active",
    dateOfBirth: null,
    gender: '',
    supervisorEmpId: '',
    address: '',
    pfApplicable: false,
    allowanceApplicable: false
  };

  const [employeeForm, setEmployeeForm] = useState(initialEmployeeForm);
  const [supervisors, setSupervisors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Attendance form (kept minimal)
  const [attendanceForm, setAttendanceForm] = useState({ date: new Date(), status: 'present', notes: '' });

  // Salary form (kept minimal)
  const [salaryForm, setSalaryForm] = useState({ month: new Date(), basicSalary: 0, allowances: 0, deductions: 0, bonus: 0 });

  // Status options
  const statusOptions = [
    { label: 'All', value: '' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  const loadDepartments = async () => {
    try {
      const res = await employeesAPI.getDepartments();
      if (res?.data?.success) {
        const depts = res.data.data || [];
        setDepartmentOptions(depts.map(d => ({ label: d.name, value: d.id })));
      } else {
        setDepartmentOptions([]);
      }
    } catch (err) {
      console.error('Failed to load departments:', err);
      setDepartmentOptions([]);
    }
  };

  const loadSalarySummary = async () => {
    try {
      const res = await employeesAPI.getSalaryStatusSummary();
      if (res?.data?.success) {
        setSalarySummary(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load salary summary:', err);
    }
  };

  useEffect(() => {
    loadDepartments();
    loadSalarySummary();
  }, []);

  useEffect(() => {
    loadLazyData();
  }, [lazyParams]);

  const buildQueryParams = () => {
    const params = {
      page: lazyParams.page || 1,
      limit: lazyParams.rows || 10,
      search: lazyParams.filters?.global?.value || '',
      departmentId: lazyParams.filters?.department?.value || '',
      status: lazyParams.filters?.status?.value || ''
    };

    if (lazyParams.sortField) {
      params.sortField = lazyParams.sortField;
      params.sortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';
    }

    return params;
  };

  const loadLazyData = async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const res = await employeesAPI.getEmployeesByParamas(params);

      if (res?.data?.success) {
        const data = res.data.data || {};
        setEmployees(data.employees || []);
        setTotalRecords(data.pagination?.totalRows);
      } else {
        setEmployees([]);
        setTotalRecords(0);
        toast.current?.show({ severity: 'warn', summary: 'Warning', detail: res?.data?.message || 'No data' });
      }
    } catch (err) {
      console.error('Failed to load employees', err);
      setEmployees([]);
      setTotalRecords(0);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to load employees' });
    } finally {
      setLoading(false);
    }
  };

  // DataTable event handlers
  const onPage = (event) => {
    setLazyParams((p) => ({ ...p, first: event.first, rows: event.rows, page: Math.floor(event.first / event.rows) + 1 }));
  };

  const onSort = (event) => {
    setLazyParams((p) => ({ ...p, sortField: event.sortField, sortOrder: event.sortOrder }));
  };

  const onFilter = (event) => {
    setLazyParams((p) => ({ ...p, first: 0, page: 1, filters: event.filters }));
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value || '';
    setLazyParams((p) => ({ ...p, first: 0, page: 1, filters: { ...p.filters, global: { value, matchMode: FilterMatchMode.CONTAINS } } }));
  };

  const onDepartmentFilterChange = (value) => {
    setLazyParams((p) => ({ ...p, first: 0, page: 1, filters: { ...p.filters, department: { value, matchMode: FilterMatchMode.EQUALS } } }));
  };

  const onStatusFilterChange = (value) => {
    setLazyParams((p) => ({ ...p, first: 0, page: 1, filters: { ...p.filters, status: { value: value === '' ? null : value, matchMode: FilterMatchMode.EQUALS } } }));
  };

  const loadSupervisors = async () => {
    try {
      const res = await employeesAPI.getSupervisors();
      if (res?.data?.success) {
        setSupervisors(res.data.data || []);
      } else {
        setSupervisors([]);
      }
    } catch (err) {
      console.error('Failed to load supervisors:', err);
      setSupervisors([]);
    }
  };

  // CRUD handlers
  const handleCreateEmployee = async () => {
    setEmployeeForm(initialEmployeeForm);
    setEditingEmployee(null);
    await loadSupervisors();
    setEmployeeDialog(true);
  };

  const handleEditEmployee = async (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      email: emp.email || '',
      phone: emp.phone || '',
      departmentId: emp.departmentId || '',
      designation: emp.designation || '',
      joiningDate: emp.joiningDate ? new Date(emp.joiningDate) : null,
      salary: emp.salary || 0,
      photoUrl: emp.photoUrl || '',
      status: emp.status === 'active' ? 'active' : 'inactive',
      dateOfBirth: emp.dateOfBirth ? new Date(emp.dateOfBirth) : null,
      gender: emp.gender || '',
      supervisorEmpId: emp.supervisorEmpId || '',
      address: emp.address || '',
      pfApplicable: emp.pfApplicable || false,
      allowanceApplicable: emp.allowanceApplicable || false
    });
    await loadSupervisors();
    setEmployeeDialog(true);
  };

  const handleSaveEmployee = async () => {
    if (!employeeForm.firstName || !employeeForm.lastName || !employeeForm.email || !employeeForm.phone || !employeeForm.departmentId) {
      toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill all required fields' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...employeeForm, supervisorEmpId: employeeForm.supervisorEmpId || null };
      if (editingEmployee) {
        await employeesAPI.updateEmployee(editingEmployee.id, payload);
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Employee updated successfully' });
      } else {
        await employeesAPI.addEmployee(payload);
        toast.current?.show({ severity: 'success', summary: 'Created', detail: 'Employee created successfully & credentials sent to email' });
      }
      setEmployeeDialog(false);
      loadLazyData();
    } catch (err) {
      console.error('Failed to save employee:', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to save employee' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEmployee = (emp) => {
    confirmDialog({
      message: `Delete ${emp.firstName} ${emp.lastName}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await employeesAPI.deleteEmployee(emp.id);
          toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Employee deleted successfully' });
          loadLazyData();
        } catch (err) {
          console.error('Delete failed', err);
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to delete' });
        }
      },
      acceptClassName: 'bg-red-600 hover:bg-red-700 text-white font-semibold px-6 mx-5 py-2 rounded-lg transition-all shadow-md',
      rejectClassName: 'bg-gray-400 hover:bg-gray-500 text-white font-semibold px-6 py-2 rounded-lg transition-all shadow-md',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel'
    });
  };

  const handleViewEmployeeDetails = (emp) => {
    setDetailsEmployee(emp);
    setShowDetailsView(true);
  };

  const handleBackToTable = () => {
    setShowDetailsView(false);
    setDetailsEmployee(null);
  };

  const handleMarkAttendance = (emp) => {
    setSelectedEmployee(emp);
    setAttendanceForm({ date: new Date(), status: 'present', notes: '' });
    setAttendanceDialog(true);
  };

  const handleProcessSalary = (emp) => {
    confirmDialog({
      message: `Are you sure you want to process salary for ${emp.firstName} ${emp.lastName}?`,
      header: 'Process Salary Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-success font-semibold px-4 py-2 mx-2',
      rejectClassName: 'p-button-text font-semibold px-4 py-2',
      accept: async () => {
        try {
          const today = new Date().toISOString().split('T')[0];
          await employeesAPI.processSalary({ employeeId: emp.id, month: today });
          toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Salary processed successfully' });
          loadLazyData();
          loadSalarySummary();
        } catch (err) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to process salary' });
        }
      }
    });
  };

  const handleMarkAttendanceSubmit = () => {
    setAttendanceDialog(false);
    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Attendance marked' });
  };

  const handleProcessSalarySubmit = () => {
    setSalaryDialog(false);
    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Salary processed' });
    loadSalarySummary();
  };

  const handlePaySalary = (emp) => {
    let mode = 'Cash';
    const modes = [
      { label: 'Cash', value: 'Cash' },
      { label: 'Card', value: 'Card' },
      { label: 'UPI', value: 'UPI' }
    ];

    confirmDialog({
      header: 'Salary Payment',
      // icon: 'pi pi-wallet text-2xl text-blue-600',
      message: (
        <div className="flex flex-col space-y-4 min-w-[300px]">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-blue-100">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                {emp.firstName.charAt(0)}{emp.lastName.charAt(0)}
              </div>
              <div>
                <p className="text-gray-500 text-xs font-medium">Employee</p>
                <p className="text-blue-900 font-bold">{emp.firstName} {emp.lastName}</p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 text-sm">Salary Amount</span>
              <span className="text-xl font-black text-blue-600">₹{emp.salary.toLocaleString()}</span>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
              <i className="pi pi-credit-card text-blue-500"></i> Payment Mode
            </label>
            <Dropdown
              value={mode}
              options={modes}
              onChange={(e) => { mode = e.value; }}
              placeholder="Select Payment Method"
              className="w-full border-2 border-gray-100 bg-gray-50 rounded-xl overflow-hidden focus:border-blue-400 transition-all"
            />
          </div>

          <p className="text-[11px] text-gray-400 italic text-center">
            * This action will mark the salary as paid for the current month.
          </p>
        </div>
      ),
      acceptLabel: 'Confirm Payment',
      rejectLabel: 'Cancel',
      acceptClassName: 'bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2.5 rounded-xl transition-all shadow-lg hover:shadow-blue-200 ml-3',
      rejectClassName: 'p-button-text text-gray-400 font-bold hover:text-gray-600 px-6 py-2.5 rounded-xl transition-all',
      accept: async () => {
        try {
          const today = new Date().toISOString().split('T')[0];
          await employeesAPI.paySalary({
            employeeId: emp.id,
            salaryMonth: today,
            amount: emp.salary,
            mode: mode
          });
          toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Salary payment done successfully' });
          loadLazyData();
          loadSalarySummary();
        } catch (err) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to pay salary' });
        }
      }
    });
  };

  const handleCreateMonthlySalary = async () => {
    confirmDialog({
      message: 'Are you sure you want to generate salary records for all active employees for this month?',
      header: 'Generate Monthly Salary',
      icon: 'pi pi-calendar-plus',
      acceptClassName: 'bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-2 rounded-lg transition-all shadow-md ml-3',
      rejectClassName: 'p-button-text text-gray-500 font-bold px-4 py-2',
      accept: async () => {
        try {
          setLoading(true);
          const res = await employeesAPI.createMonthlySalary();
          if (res?.data?.success) {
            toast.current?.show({ severity: 'success', summary: 'Success', detail: res.data.message });
            loadSalarySummary();
            loadLazyData();
          }
        } catch (err) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.message || 'Failed to create monthly salary' });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleViewSalaryHistory = async (emp) => {
    try {
      setSelectedEmployee(emp);
      const res = await employeesAPI.getSalaryHistory(emp.id);
      if (res?.data?.success) {
        setSalaryHistory(res.data.data);
        setSalaryHistoryDialog(true);
      }
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch salary history' });
    }
  };

  const actionBodyTemplate = (rowData) => (
    <div className="flex items-center justify-center space-x-2">
      <Button icon="pi pi-eye" className="p-button-rounded p-button-text p-button-sm hover:bg-blue-50 text-blue-600 border-2 border-blue-200 hover:border-blue-300 transition-all duration-200" onClick={() => handleViewEmployeeDetails(rowData)} tooltip="View Details" tooltipOptions={{ position: 'top' }} />
      <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm hover:bg-green-50 text-green-600 border-2 border-green-200 hover:border-green-300 transition-all duration-200" onClick={() => handleEditEmployee(rowData)} tooltip="Edit Employee" tooltipOptions={{ position: 'top' }} />
      <Button icon="pi pi-history" className="p-button-rounded p-button-text p-button-sm hover:bg-orange-50 text-orange-600 border-2 border-orange-200 hover:border-orange-300 transition-all duration-200" onClick={() => handleViewSalaryHistory(rowData)} tooltip="Salary History" tooltipOptions={{ position: 'top' }} />
      <Button icon="pi pi-calendar" className="p-button-rounded p-button-text p-button-sm hover:bg-purple-50 text-purple-600 border-2 border-purple-200 hover:border-purple-300 transition-all duration-200" onClick={() => handleMarkAttendance(rowData)} tooltip="Mark Attendance" tooltipOptions={{ position: 'top' }} />
      <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm hover:bg-red-50 text-red-600 border-2 border-red-200 hover:border-red-300 transition-all duration-200" onClick={() => handleDeleteEmployee(rowData)} tooltip="Delete Employee" tooltipOptions={{ position: 'top' }} />
      {rowData.salaryStatus === 'paid' ? (
        <div className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-green-200 bg-green-50 text-green-600" title="Salary Paid">
          <i className="pi pi-check text-xs font-bold" />
        </div>
      ) : rowData.salaryStatus === 'processed' ? (
        <Button icon="pi pi-wallet" className="p-button-rounded p-button-text p-button-sm hover:bg-cyan-50 text-cyan-600 border-2 border-cyan-200 hover:border-cyan-300 transition-all duration-200"
          onClick={() => handlePaySalary(rowData)} tooltip="Pay Salary" tooltipOptions={{ position: 'top' }} />
      ) : (
        // <Button icon="pi pi-dollar" className="p-button-rounded p-button-text p-button-sm hover:bg-yellow-50 text-yellow-600 border-2 border-yellow-200 hover:border-yellow-300  transition-all duration-200"
        //   onClick={() => handleProcessSalary(rowData)} tooltip="Process Salary" tooltipOptions={{ position: 'top' }}/>
        <Button label="₹" onClick={() => handleProcessSalary(rowData)} tooltip="Process Salary" tooltipOptions={{ position: 'top' }}
          className=" w-10 h-10  rounded-full  flex items-center justify-center  bg-yellow-500 hover:bg-yellow-600  text-white font-semibold  shadow-md  transition-all duration-200" />
      )}
    </div>
  );

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <EmployeeFilter
        user={user}
        handleCreateEmployee={handleCreateEmployee}
        handleCreateMonthlySalary={handleCreateMonthlySalary}
        salarySummary={salarySummary}
        lazyParams={lazyParams}
        onGlobalFilterChange={onGlobalFilterChange}
        departmentOptions={departmentOptions}
        onDepartmentFilterChange={onDepartmentFilterChange}
        statusOptions={statusOptions}
        onStatusFilterChange={onStatusFilterChange}
        showDetailsView={showDetailsView}
      />

      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl overflow-hidden flex flex-col flex-1 min-h-0 mt-2">
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading && employees.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="pi pi-users text-blue-600 text-xl"></i>
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <span className="text-lg font-bold text-gray-900">Loading Employees</span>
                  <span className="text-sm text-gray-500 italic">Please wait while we fetch the directory...</span>
                </div>
              </div>
            </div>
          ) : showDetailsView && detailsEmployee ? (
            <EmployeeDetails
              detailsEmployee={detailsEmployee}
              handleBackToTable={handleBackToTable}
            />
          ) : (
            <EmployeesTable
              employees={employees}
              totalRecords={totalRecords}
              onPage={onPage}
              onSort={onSort}
              onFilter={onFilter}
              lazyParams={lazyParams}
              loading={loading}
              actionBodyTemplate={actionBodyTemplate}
            />
          )}
        </div>
        {!showDetailsView && (
          <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-6 text-sm font-medium">
            <div className="flex items-center">
              <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
              <span className="text-gray-600">Paid:</span>
              <span className="ml-1 text-gray-900">{salarySummary.paid}</span>
            </div>
            <div className="flex items-center">
              <span className="w-3 h-3 rounded-full bg-cyan-500 mr-2"></span>
              <span className="text-gray-600">Processed:</span>
              <span className="ml-1 text-gray-900">{salarySummary.processed}</span>
            </div>
            <div className="flex items-center">
              <span className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>
              <span className="text-gray-600">Pending:</span>
              <span className="ml-1 text-gray-900">{salarySummary.pending}</span>
            </div>
            <div className="flex items-center border-l pl-6">
              <span className="text-gray-600 font-bold">Total:</span>
              <span className="ml-1 text-gray-900 font-bold">{salarySummary.total}</span>
            </div>
          </div>
        )}
      </div>

      <EmployeeFormDialog
        visible={employeeDialog}
        onHide={() => setEmployeeDialog(false)}
        editingEmployee={editingEmployee}
        employeeForm={employeeForm}
        setEmployeeForm={setEmployeeForm}
        departmentOptions={departmentOptions}
        supervisors={supervisors}
        submitting={submitting}
        handleSaveEmployee={handleSaveEmployee}
      />

      <AttendanceDialog
        visible={attendanceDialog}
        onHide={() => setAttendanceDialog(false)}
        selectedEmployee={selectedEmployee}
        attendanceForm={attendanceForm}
        setAttendanceForm={setAttendanceForm}
        handleMarkAttendanceSubmit={handleMarkAttendanceSubmit}
      />

      <SalaryDialog
        visible={salaryDialog}
        onHide={() => setSalaryDialog(false)}
        selectedEmployee={selectedEmployee}
        salaryForm={salaryForm}
        setSalaryForm={setSalaryForm}
        handleProcessSalarySubmit={handleProcessSalarySubmit}
      />

      <SalaryHistoryDialog
        visible={salaryHistoryDialog}
        onHide={() => setSalaryHistoryDialog(false)}
        employee={selectedEmployee}
        history={salaryHistory}
      />
    </div>
  );
};

export default Employees;
